import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { LinearGradient } from 'expo-linear-gradient';
import { Paprika, PaprikaZones, PaprikaShadows, PaprikaTypography } from '../../../styles/theme';
import { PantryLocation } from '../../../utils/pantryLocation';
import { ZONE_META } from '../zoneMeta';
import { PantryIcon } from '../PantryIcon';
import { UrgencyBadge } from '../UrgencyBadge';

interface Props {
  zone: PantryLocation;
  count: number;
  urgentCount: number;
  onPress: () => void;
}

export function ZoneTile({ zone, count, urgentCount, onPress }: Props) {
  const { t } = useTranslation();
  const z = PaprikaZones[zone];
  const meta = ZONE_META[zone];

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.tile,
        { borderColor: `${z.accent}22` },
        pressed && styles.pressed,
      ]}
    >
      <LinearGradient
        colors={[z.gradStart, z.gradEnd]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0.34, y: 1 }}
        style={styles.gradient}
      />
      <View style={styles.watermark} pointerEvents="none">
        <PantryIcon icon={meta.icon} size={104} color={z.accent} />
      </View>
      <View style={styles.topRow}>
        <View style={[styles.iconChip, { backgroundColor: Paprika.glassWhite }]}>
          <PantryIcon icon={meta.icon} size={22} color={z.accent} />
        </View>
        <UrgencyBadge count={urgentCount} />
      </View>
      <View style={styles.bottom}>
        <Text style={styles.name}>{t(meta.labelKey)}</Text>
        <Text style={[styles.count, { color: z.ink }]}>
          {t('pantry.itemsCount', { count })}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  tile: {
    flex: 1,
    minHeight: 132,
    borderRadius: 18,
    borderWidth: 1,
    padding: 14,
    overflow: 'hidden',
    justifyContent: 'space-between',
    ...PaprikaShadows.card,
  },
  gradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  pressed: {
    transform: [{ scale: 0.97 }],
  },
  watermark: {
    position: 'absolute',
    right: -14,
    bottom: -18,
    opacity: 0.12,
    transform: [{ rotate: '-8deg' }],
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    zIndex: 10,
  },
  iconChip: {
    width: 38,
    height: 38,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottom: {
    marginTop: 'auto',
    zIndex: 10,
  },
  name: {
    ...PaprikaTypography.headingMedium,
    fontSize: 21,
    lineHeight: 24,
    color: Paprika.textPrimary,
  },
  count: {
    fontFamily: 'Inter-Medium',
    fontSize: 11.5,
    marginTop: 5,
  },
});
